import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";
import {
  Flex,
  Alert,
  AlertDescription,
  Grid,
  Button,
  Tooltip,
  Center,
  useMediaQuery,
  useDisclosure,
  Heading
} from "@chakra-ui/react";
import PuffLoader from "react-spinners/PuffLoader";
import { format } from "date-fns";

import { Page } from "components/page";
import { SchedulePanel, PublishedPanel, DraftsPanel, ErrorsPanel } from "../queue-panels";
import { ConfirmPopup } from "../popups/confirmPopup";
import { getNextSlots, rebuildQueue } from "controllers/schedule";
import { Slots } from "types/schedule";
import { getDateFromSecondsAndNanoSeconds } from "utils/helpers";
import { useFirebaseUser } from "utils/useFirebaseUser";
import { useTweetContext } from "context/tweetContext";
import { variant } from "theme/names";
import * as analytics from "utils/analytics";
import { PostTypesDropdown } from "./post-types-dropdown";
import { ActionsDropdown } from "./actions-dropdown";

interface Props {
  query: any;
  selectedType: string;
  onTypeChange: (newType: string) => void;
}

const DEFAULT_TYPE = "schedule";

export function DayView({ query, selectedType, onTypeChange }: Props) {
  const [slots, setSlots] = useState<Slots[]>([]);
  const [scheduleTweets, setScheduleTweets] = useState<any>([]);
  const [firstEmptyDay, setFirstEmptyDay] = useState<any>(new Date());
  const [nbSlots, setNbSlots] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInit, setHasInit] = useState<boolean>(false);
  const { data: session } = useSession() ?? {}
  const isAuthenticated = useFirebaseUser();
  const tweetContext: any = useTweetContext();



  const getSlots = async function () {
    let props = await getNextSlots(session, undefined, 365);
    const { slots, firstEmptyDay, scheduleTweets, nbSlots } = props;
    setSlots(slots);
    setFirstEmptyDay(firstEmptyDay);
    setScheduleTweets(scheduleTweets);
    setNbSlots(nbSlots);
    setIsLoading(false);
    setHasInit(true);
  };

  useEffect(() => {
    if (isAuthenticated) {
      getSlots();
    }
  }, [isAuthenticated, tweetContext.refresh])

  const tweetsScheduledTime = scheduleTweets.length > 0 && scheduleTweets.map((tweet) => ({
    time: tweet.scheduledTime
  })).sort((a, b) => b.time - a.time)


  const dateOfTheLastScheduledTweet = scheduleTweets.length > 0 && format(
    getDateFromSecondsAndNanoSeconds(tweetsScheduledTime[0].time.seconds, tweetsScheduledTime[0].time.nanoseconds)
    , `EEEE MMMM do${new Date(getDateFromSecondsAndNanoSeconds(tweetsScheduledTime[0].time.seconds, tweetsScheduledTime[0].time.nanoseconds)).getFullYear() > new Date().getFullYear() ? " yyyy" : ""}`)


  return (
    <div>
      {hasInit && (
        <Flex mt={6}>
          {scheduleTweets?.length > 0 && firstEmptyDay ? (
            <Alert status="success">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.59375 10.625C7.25 10.9688 6.71875 10.9688 6.375 10.625L4.375 8.625C4.03125 8.28125 4.03125 7.75 4.375 7.40625C4.71875 7.0625 5.25 7.0625 5.59375 7.40625L7 8.78125L10.375 5.40625C10.7188 5.0625 11.25 5.0625 11.5938 5.40625C11.9375 5.75 11.9375 6.28125 11.5938 6.625L7.59375 10.625ZM16 8C16 12.4375 12.4062 16 8 16C3.5625 16 0 12.4375 0 8C0 3.59375 3.5625 0 8 0C12.4062 0 16 3.59375 16 8ZM8 1.5C4.40625 1.5 1.5 4.4375 1.5 8C1.5 11.5938 4.40625 14.5 8 14.5C11.5625 14.5 14.5 11.5938 14.5 8C14.5 4.4375 11.5625 1.5 8 1.5Z" fill="#31C52E" />
              </svg>
              <AlertDescription p="2">
                {scheduleTweets.length > 1
                  ? (<>You have {scheduleTweets.length} posts scheduled. The last one will be published on <b>{dateOfTheLastScheduledTweet}</b></>)
                  : (<>You have 1 post scheduled and it will be published on <b>{dateOfTheLastScheduledTweet}</b></>)
                }
              </AlertDescription>
            </Alert>
          ) : (
            <Alert status="error">
              <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.8125 13.0312C17.3125 13.9062 16.6875 15 15.6562 15H2.3125C1.28125 15 0.65625 13.9062 1.125 13.0312L7.8125 1.65625C8.09375 1.21875 8.53125 1 9 1C9.4375 1 9.875 1.21875 10.1562 1.65625L16.8125 13.0312ZM2.625 13.5H15.3438L8.96875 2.65625L2.625 13.5ZM9 10.5625C9.53125 10.5625 9.96875 11 9.96875 11.5312C9.96875 12.0625 9.53125 12.5 9 12.5C8.4375 12.5 8 12.0625 8 11.5312C8 11 8.4375 10.5625 9 10.5625ZM8.25 5.75C8.25 5.34375 8.5625 5 9 5C9.40625 5 9.75 5.34375 9.75 5.75V8.75C9.75 9.1875 9.40625 9.5 9 9.5C8.5625 9.5 8.25 9.1875 8.25 8.75V5.75Z" fill="#EC4343" />
              </svg>
              <AlertDescription p="2">
                You have no scheduled posts in your queue
              </AlertDescription>
            </Alert>
          )}
        </Flex>
      )}
      <div>
        {selectedType === "schedule" && (
          isLoading ? (
            <Center pt={20}>
              <PuffLoader
                color={"#09527c"}
                loading={isLoading}
                cssOverride={{
                  display: "block",
                  margin: "0 auto",
                  borderColor: "red",
                }}
                size={50}
              />
            </Center>
          ) : (
            <SchedulePanel
              slots={slots}
              setSlots={setSlots}
              nbSlots={nbSlots}
            />
          )
        )}
        {selectedType === "published" && <PublishedPanel />}
        {selectedType === "drafts" && <DraftsPanel isSelected={selectedType === "drafts"} />}
        {selectedType === "errors" && <ErrorsPanel isSelected={selectedType === "errors"} />}
      </div>

    </div>
  );
}
